import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { apiHost } from '../../utils/apiHost';

// Refund / retur per item untuk admin cabang & superadmin (kasir tidak punya akses; server menolak 403).
// Server: controller/refundController.js. Transaksi asli dikurangi di tempat, jadi laporan otomatis bersih.

const base = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  return `${protocol}://${apiHost().replace(/^https?:\/\//, '')}`;
};
const rupiah = (v) => `Rp ${Math.round(Number(v) || 0).toLocaleString('id-ID')}`;
const jam = (iso) => (iso ? new Date(iso).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-');
const idBaru = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `rf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

const RefundDialog = ({ transaksiUuid, open, onClose, onDone }) => {
  const [info, setInfo] = useState(null);
  const [galat, setGalat] = useState('');
  const [jumlah, setJumlah] = useState({}); // detailuuid -> jumlah refund (teks)
  const [stok, setStok] = useState({}); // detailuuid -> kembalikan stok
  const [alasan, setAlasan] = useState('');
  const [catatan, setCatatan] = useState('');
  const [proses, setProses] = useState(false);
  const [clientId, setClientId] = useState(idBaru);

  useEffect(() => {
    if (!open || !transaksiUuid) return undefined;
    let batal = false;
    setInfo(null); setGalat(''); setJumlah({}); setStok({}); setAlasan(''); setCatatan(''); setClientId(idBaru());
    axios.get(`${base()}/refund/transaksi/${transaksiUuid}`, { withCredentials: true })
      .then((r) => { if (!batal) setInfo(r.data.data); })
      .catch((e) => { if (!batal) setGalat(e?.response?.data?.message || 'Gagal memuat transaksi'); });
    return () => { batal = true; };
  }, [open, transaksiUuid]);

  const items = useMemo(() => info?.items || [], [info]);
  const dipilih = items
    .map((i) => ({ i, n: Number(jumlah[i.detailuuid] || 0) }))
    .filter((x) => x.n > 0);
  const valid = dipilih.every(({ i, n }) => Number.isInteger(n) && n >= 1 && n <= i.jumlah && (!i.bernomorSeri || n === i.jumlah));
  const subtotal = dipilih.reduce((a, { i, n }) => a + (n === i.jumlah ? i.total : (i.total / i.jumlah) * n), 0);
  const ppn = info?.ppnPersen ? Math.round((subtotal * info.ppnPersen) / 100) : 0;
  const semua = items.length > 0 && items.every((i) => Number(jumlah[i.detailuuid] || 0) === i.jumlah);
  const siap = info?.bisaRefund && dipilih.length > 0 && valid && alasan && (alasan !== 'lainnya' || catatan.trim().length >= 3);

  const kirim = async () => {
    setProses(true); setGalat('');
    try {
      const r = await axios.post(`${base()}/refund/transaksi/${transaksiUuid}`, {
        clientId,
        alasan,
        catatan: catatan.trim() || undefined,
        items: dipilih.map(({ i, n }) => ({
          detailuuid: i.detailuuid,
          jumlah: n,
          kembalikanStok: stok[i.detailuuid] ?? info.kembalikanStokBawaan,
        })),
      }, { withCredentials: true });
      onDone?.(r.data.message || 'Refund dicatat');
      onClose();
    } catch (e) {
      setGalat(e?.response?.data?.message || e?.message || 'Gagal menyimpan refund');
    } finally {
      setProses(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !proses && onClose()} fullWidth maxWidth="md">
      <DialogTitle>Refund per item{info ? ` · ${info.orderId}` : ''}</DialogTitle>
      <DialogContent>
        {galat && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGalat('')}>{galat}</Alert>}
        {!info && !galat && <Typography>Memuat…</Typography>}
        {info && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {[info.namacabang, `Dibayar ${info.pembayaran}`, `Total sekarang ${rupiah(info.totalSekarang)}`, info.totalSudahDirefund > 0 && `Sudah direfund ${rupiah(info.totalSudahDirefund)}`].filter(Boolean).join(' · ')}
            </Typography>
            {!info.bisaRefund && <Alert severity="warning" sx={{ mb: 2 }}>{info.alasanTidakBisa}</Alert>}
            {info.catatanMetode && <Alert severity="info" sx={{ mb: 2 }}>{info.catatanMetode}</Alert>}
            {info.bisaRefund && (
              <>
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Barang</TableCell>
                        <TableCell align="right">Dibeli</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell sx={{ width: 120 }}>Jumlah refund</TableCell>
                        <TableCell>Stok</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((i) => {
                        const n = Number(jumlah[i.detailuuid] || 0);
                        const salah = n > 0 && (n > i.jumlah || (i.bernomorSeri && n !== i.jumlah));
                        return (
                          <TableRow key={i.detailuuid}>
                            <TableCell>
                              {i.namabarang}
                              {i.bernomorSeri && <Chip size="small" label="bernomor seri: refund seluruhnya" sx={{ ml: 1 }} />}
                            </TableCell>
                            <TableCell align="right">{i.jumlah}</TableCell>
                            <TableCell align="right">{rupiah(i.total)}</TableCell>
                            <TableCell>
                              <TextField
                                size="small" type="number" value={jumlah[i.detailuuid] ?? ''} error={salah}
                                inputProps={{ min: 0, max: i.jumlah, step: 1, 'aria-label': `Jumlah refund ${i.namabarang}` }}
                                onChange={(e) => setJumlah((s) => ({ ...s, [i.detailuuid]: e.target.value }))}
                              />
                            </TableCell>
                            <TableCell>
                              {n > 0 && (
                                <FormControlLabel
                                  control={<Checkbox size="small" checked={stok[i.detailuuid] ?? info.kembalikanStokBawaan} onChange={(e) => setStok((s) => ({ ...s, [i.detailuuid]: e.target.checked }))} />}
                                  label="Kembali ke stok"
                                />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                  <TextField select size="small" label="Alasan refund" value={alasan} onChange={(e) => setAlasan(e.target.value)}>
                    {info.alasan.map((a) => <MenuItem key={a.key} value={a.key}>{a.label}</MenuItem>)}
                  </TextField>
                  <TextField
                    size="small" label={alasan === 'lainnya' ? 'Catatan (wajib)' : 'Catatan (opsional)'} value={catatan}
                    inputProps={{ maxLength: 255 }} onChange={(e) => setCatatan(e.target.value)}
                    error={alasan === 'lainnya' && catatan.trim().length < 3}
                  />
                </Box>
                {semua && <Alert severity="warning" sx={{ mb: 2 }}>Semua barang direfund: transaksi ini akan dianggap dibatalkan.</Alert>}
                <Typography variant="subtitle1" fontWeight={700}>
                  Uang dikembalikan: {rupiah(subtotal + ppn)}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {info.ppnPersen ? `(termasuk PPN ${info.ppnPersen}%, perkiraan; angka pasti dihitung server)` : ''}
                  </Typography>
                </Typography>
              </>
            )}
            {info.riwayat.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Riwayat refund transaksi ini</Typography>
                {info.riwayat.map((r) => (
                  <Typography key={r.uuid} variant="body2" color="text.secondary">
                    {jam(r.createdAt)} · {rupiah(r.totalRefund)} · {r.alasanLabel}{r.catatan ? ` (${r.catatan})` : ''}
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={proses}>Tutup</Button>
        {info?.bisaRefund && <Button variant="contained" color="warning" onClick={kirim} disabled={!siap || proses}>{proses ? 'Menyimpan…' : 'Proses refund'}</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default RefundDialog;
